import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CACHE_TIME } from '@hygge/shared';
import type { WeeklyRosterResponse } from '@hygge/shared';
import { extractErrorMessage } from '@/lib/errors';
import { boardKeys } from '@/hooks/use-boards';
import { timeEntryKeys } from '@/hooks/use-time-entries';
import { useAuthStore } from '@/stores/auth';
import {
  getProjects,
  getProject,
  getProjectStats,
  createProject,
  updateProject,
  archiveProject,
  type ArchiveBoardChoice,
  generateSlug,
  getProjectMembers,
  addProjectMembers,
  removeProjectMember,
  reorderProjectMembers,
  updateProjectMember,
  reopenMemberPeriod,
  getProjectActivity,
  getRoster,
  upsertRoster,
  getFinancials,
  getRosterForecast,
  upsertRosterForecast,
  getForecastSummary,
  getPlanVsActual,
  type ProjectsQueryParams,
} from '@/lib/projects';

export const projectsKeys = {
  all: ['projects'] as const,
  lists: () => [...projectsKeys.all, 'list'] as const,
  list: (params: ProjectsQueryParams) => [...projectsKeys.lists(), params] as const,
  stats: () => [...projectsKeys.all, 'stats'] as const,
  details: () => [...projectsKeys.all, 'detail'] as const,
  detail: (id: string) => [...projectsKeys.details(), id] as const,
  members: (id: string) => [...projectsKeys.detail(id), 'members'] as const,
  activity: (id: string) => [...projectsKeys.detail(id), 'activity'] as const,
  roster: (id: string, month: string) => [...projectsKeys.detail(id), 'roster', month] as const,
  rosterForecast: (id: string, month: string) =>
    [...projectsKeys.detail(id), 'roster-forecast', month] as const,
  financials: (id: string, month: string) => [...projectsKeys.detail(id), 'financials', month] as const,
  forecastSummary: (id: string) => [...projectsKeys.detail(id), 'forecast-summary'] as const,
  planVsActual: (id: string, month: string) => [...projectsKeys.detail(id), 'plan-vs-actual', month] as const,
};

export function useProjects(params: ProjectsQueryParams = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: projectsKeys.list(params),
    queryFn: () => getProjects(params),
    staleTime: CACHE_TIME.DEFAULT,
    enabled: options.enabled ?? true,
  });
}

export function useProject(id: string) {
  return useQuery({
    queryKey: projectsKeys.detail(id),
    queryFn: () => getProject(id),
    staleTime: CACHE_TIME.DEFAULT,
    enabled: !!id,
  });
}

export function useProjectStats() {
  return useQuery({
    queryKey: projectsKeys.stats(),
    queryFn: getProjectStats,
    staleTime: CACHE_TIME.DEFAULT,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createProject,
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectsKeys.stats() });
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }: Parameters<typeof updateProject>[1] & { id: string }) =>
      updateProject(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: projectsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectsKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: projectsKeys.stats() });
      if (variables.archiveBoards) queryClient.invalidateQueries({ queryKey: boardKeys.all });
    },
  });
}

export function useArchiveProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, boards }: { id: string; boards?: ArchiveBoardChoice }) => archiveProject(id, boards),
    onSuccess: (_data, { id, boards }) => {
      queryClient.invalidateQueries({ queryKey: projectsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: projectsKeys.stats() });
      // The boards were archived or unlinked along with the project, and its
      // Activity no longer carries the lines of boards it let go.
      if (boards) {
        queryClient.invalidateQueries({ queryKey: boardKeys.all });
        queryClient.invalidateQueries({ queryKey: projectsKeys.activity(id) });
      }
    },
  });
}

export function useGenerateSlug() {
  return useMutation({
    mutationFn: generateSlug,
  });
}

export function useProjectMembers(projectId: string) {
  return useQuery({
    queryKey: projectsKeys.members(projectId),
    queryFn: () => getProjectMembers(projectId),
    staleTime: CACHE_TIME.DEFAULT,
    enabled: !!projectId,
  });
}

export function useAddProjectMembers(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (employeeIds: string[]) => addProjectMembers(projectId, employeeIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectsKeys.members(projectId) });
      queryClient.invalidateQueries({ queryKey: projectsKeys.detail(projectId) });
      queryClient.invalidateQueries({ queryKey: projectsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectsKeys.stats() });
    },
  });
}

export function useRemoveProjectMember(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (employeeId: string) => removeProjectMember(projectId, employeeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectsKeys.members(projectId) });
      queryClient.invalidateQueries({ queryKey: projectsKeys.detail(projectId) });
      queryClient.invalidateQueries({ queryKey: projectsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectsKeys.stats() });
    },
  });
}

export function useUpdateProjectMember(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      employeeId,
      ...data
    }: {
      employeeId: string;
      joinedAt?: string;
      removedAt?: string | null;
      isManager?: boolean;
    }) => updateProjectMember(projectId, employeeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectsKeys.members(projectId) });
      queryClient.invalidateQueries({ queryKey: projectsKeys.detail(projectId) });
      queryClient.invalidateQueries({ queryKey: projectsKeys.lists() });
    },
  });
}

/**
 * Reopen a closed period for a member. The members list shows the window and
 * the feed its row, so both are refetched; a refusal reaches the global
 * mutation toast and leaves the dialog open. A holder who opens one for
 * themselves also refetches their own windows and their entries, whose edit
 * and delete flags the window changes. [HW-963]
 */
export function useReopenMemberPeriod(projectId: string) {
  const queryClient = useQueryClient();
  const viewerId = useAuthStore((s) => s.user?.id ?? '');
  const viewerEmployeeId = useAuthStore((s) => s.user?.employee?.id ?? null);
  return useMutation({
    mutationFn: ({ employeeId, ...body }: { employeeId: string; from: string; hours: number }) =>
      reopenMemberPeriod(projectId, employeeId, body),
    onSuccess: (_result, { employeeId }) => {
      queryClient.invalidateQueries({ queryKey: projectsKeys.members(projectId) });
      queryClient.invalidateQueries({ queryKey: projectsKeys.activity(projectId) });
      if (viewerEmployeeId && employeeId === viewerEmployeeId) {
        queryClient.invalidateQueries({ queryKey: timeEntryKeys.reopenedPeriods(viewerId) });
        queryClient.invalidateQueries({ queryKey: timeEntryKeys.all });
      }
      toast.success('Closed period reopened');
    },
  });
}

export function useReorderRosterMembers(projectId: string, month: string) {
  const queryClient = useQueryClient();
  const rosterKey = projectsKeys.roster(projectId, month);

  return useMutation({
    mutationFn: (employeeIds: string[]) => reorderProjectMembers(projectId, employeeIds),
    onMutate: async (newOrder) => {
      await queryClient.cancelQueries({ queryKey: rosterKey });
      const previous = queryClient.getQueryData<WeeklyRosterResponse>(rosterKey);
      if (previous) {
        const rank = new Map(newOrder.map((id, i) => [id, i]));
        const rankOf = (id: string) => rank.get(id) ?? Number.MAX_SAFE_INTEGER;
        const reordered = [...previous.entries].sort((a, b) => {
          if (a.week !== b.week) return a.week - b.week;
          return rankOf(a.employeeId) - rankOf(b.employeeId);
        });
        queryClient.setQueryData<WeeklyRosterResponse>(rosterKey, {
          ...previous,
          entries: reordered,
        });
      }
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(rosterKey, ctx.previous);
      toast.error(extractErrorMessage(err, 'Failed to reorder roster'));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: rosterKey });
      queryClient.invalidateQueries({ queryKey: projectsKeys.members(projectId) });
    },
  });
}

export function useProjectActivity(projectId: string, limit = 10) {
  return useQuery({
    queryKey: [...projectsKeys.activity(projectId), limit],
    queryFn: () => getProjectActivity(projectId, limit),
    staleTime: 15_000,
    refetchInterval: 15_000,
    enabled: !!projectId,
  });
}

export function useRoster(projectId: string, month: string) {
  return useQuery({
    queryKey: projectsKeys.roster(projectId, month),
    queryFn: () => getRoster(projectId, month),
    staleTime: CACHE_TIME.DEFAULT,
    enabled: !!projectId && !!month,
  });
}

export function useUpsertRoster(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      month,
      week,
      entries,
    }: {
      month: string;
      week: number;
      entries: {
        employeeId: string;
        realHours?: number;
        billableHours?: number;
        niceToHaveRate?: number;
        realRate?: number;
      }[];
    }) => upsertRoster(projectId, month, week, entries),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: projectsKeys.roster(projectId, variables.month) });
    },
    onError: (error) => {
      // A single rate edit can cascade into multiple per-week PUTs; collapse
      // their 400s into one toast so the user doesn't see N duplicates.
      toast.error(extractErrorMessage(error, 'Failed to save roster'), {
        id: 'roster-upsert-error',
      });
    },
  });
}

export function useRosterForecast(projectId: string, month: string) {
  return useQuery({
    queryKey: projectsKeys.rosterForecast(projectId, month),
    queryFn: () => getRosterForecast(projectId, month),
    staleTime: CACHE_TIME.DEFAULT,
    enabled: !!projectId && !!month,
  });
}

export function useUpsertRosterForecast(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: {
      month: string;
      entries: {
        employeeId: string;
        realHours?: number;
        billableHours?: number;
        niceToHaveRate?: number;
        realRate?: number;
      }[];
    }) => upsertRosterForecast(projectId, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: projectsKeys.rosterForecast(projectId, variables.month),
      });
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'Failed to save monthly forecast'), {
        id: 'roster-forecast-upsert-error',
      });
    },
  });
}

export function useFinancials(projectId: string, month: string) {
  return useQuery({
    queryKey: projectsKeys.financials(projectId, month),
    queryFn: () => getFinancials(projectId, month),
    staleTime: CACHE_TIME.DEFAULT,
    enabled: !!projectId && !!month,
  });
}

export function useForecastSummary(projectId: string) {
  return useQuery({
    queryKey: projectsKeys.forecastSummary(projectId),
    queryFn: () => getForecastSummary(projectId),
    staleTime: CACHE_TIME.DEFAULT,
    enabled: !!projectId,
  });
}

export function usePlanVsActual(projectId: string, month: string) {
  return useQuery({
    queryKey: projectsKeys.planVsActual(projectId, month),
    queryFn: () => getPlanVsActual(projectId, month),
    staleTime: CACHE_TIME.DEFAULT,
    enabled: !!projectId && !!month,
  });
}
